import { FileDownloadDTO } from '@mz-codes/mz-private-api-client';
import { useRequests } from 'app/common/contexts/requests';
import { getToken } from 'app/common/services/handleStorage';
import { getApis } from 'configs/api';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';

interface UsePdfDocumentHookProps {
  numPages: number;
  currentZoom: number;
  setCurrentZoom: Dispatch<SetStateAction<number>>;
  onDocumentLoadSuccess: ({ numPages }: { numPages: number }) => void;
  handleDownloadDocumentByUrlParams: (
    folderId: string,
    fileId: string,
    viaEmail?: boolean
  ) => Promise<boolean>;
}

const usePdfDocument = (): UsePdfDocumentHookProps => {
  const [numPages, setNumPages] = useState<number>();
  const [currentZoom, setCurrentZoom] = useState<number>(1);
  const { handleRequest } = useRequests();

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
  };

  const requestApi = useCallback(
    async (folderId: string, fileId: string, viaEmail?: boolean) => {
      return handleRequest<FileDownloadDTO>({
        requestConfig: {
          requestEffect: 'foldersApiFilesRequest',
          apiCall: () => getApis().foldersApi.retrieveFileForDownload(folderId, fileId, viaEmail),
          hideSuccessToast: true,
        },
      });
    },
    [handleRequest]
  );

  // TODO: deprecate this and [use from Documents Context] or [use a better hook to this]
  const handle = useCallback(
    async (folderId: string, fileId: string, viaEmail?: boolean) => {
      let shouldRevokeObjectUrl = false;
      const response = await requestApi(folderId, fileId, viaEmail);
      if (response === undefined) {
        return false;
      }

      const { url, name, hasWatermark } = response.data;
      const link = document.createElement('a');

      if (hasWatermark) {
        const fetchResponse = await fetch(url, {
          headers: {
            Authorization: getToken(),
          },
        });

        const blob = await fetchResponse.blob();
        link.href = URL.createObjectURL(blob);
        shouldRevokeObjectUrl = true;
      } else {
        link.href = url;
      }

      link.target = '_blank';
      link.setAttribute('download', name);

      document.body.appendChild(link);
      // TODO: external links requires user to allow popup opening. Implement a way to handle it.
      link.click();
      document.body.removeChild(link);

      if (shouldRevokeObjectUrl) {
        URL.revokeObjectURL(link.href);
      }

      return true;
    },
    [requestApi]
  );

  const handleDownloadDocumentByUrlParams = useCallback(
    async (folderId: string, fileId: string, viaEmail?: boolean) => {
      try {
        const success = await handle(folderId, fileId, viaEmail);
        return success;
      } catch (err: any) {
        // TODO: refactor API error handling
        if (err.message === 'FILE_FOLDER_NOT_FOUND') {
          throw err;
        }

        return false;
      }
    },
    [handle]
  );

  return {
    numPages,
    currentZoom,
    setCurrentZoom,
    onDocumentLoadSuccess,
    handleDownloadDocumentByUrlParams,
  };
};

export default usePdfDocument;
